import React from "react";

/**
 * How much room a card has left, and whether it is worth filling.
 *
 * Two cards on the dashboard finish well short of their own floor: the risk
 * web binds its radius to the SHORTER side of its box, so in a box taller
 * than it is wide the surplus is real and unusable by the chart; and the
 * positions ring, drilled into a group of one or two holdings, leaves the
 * list a band of paper it has nothing to put in.
 *
 * A card with that much room can afford to say one more true thing. A card
 * without it must not try, and a note that appears and disappears as the
 * page resizes is worse than no note at all, so the decision is measured
 * rather than guessed — and measured in a way that survives the note's own
 * arrival, which is the whole difficulty. See useRoomFor.
 */

/**
 * The element's box, watched — attached by a callback rather than by a ref
 * object.
 *
 * A plain `useRef` cannot do this job. The effect that starts observing runs
 * once, keyed on a ref whose identity never changes, so it sees whatever
 * `ref.current` held at that moment — and on a card whose body arrives with
 * the data, that is NULL. The observer is never started and the box stays at
 * zero for the life of the card. Measured on the positions card, where the
 * first render is an empty state and the real body mounts a request later:
 * the footer never appeared, because the card believed it was nought pixels
 * tall.
 *
 * A callback ref held in STATE re-runs the effect when the node arrives, and
 * again if the node is swapped for another — which is what a card does every
 * time a reader changes its view.
 */
export const useBox = () => {
  const [el, setEl] = React.useState<HTMLElement | null>(null);
  const [box, setBox] = React.useState({ width: 0, height: 0 });

  React.useEffect(() => {
    if (!el) return undefined;
    const read = () => {
      const next = { width: el.clientWidth, height: el.clientHeight };
      setBox((was) =>
        was.width === next.width && was.height === next.height ? was : next,
      );
    };
    read();
    const observer = new ResizeObserver(read);
    observer.observe(el);
    return () => observer.disconnect();
  }, [el]);

  return [setEl, box] as const;
};

/**
 * Would there be room for the note if the note were not already there?
 *
 * That question, and not "is there room now", is the one that has an answer.
 * A note placed in the spare room takes the spare room, so measuring after
 * it appears finds it gone — and a first attempt with two sticky thresholds
 * only hid the problem: it kept the note wherever a few pixels were left,
 * which on a card sized by its own content meant the card GREW by the
 * note's height and then reported that as room.
 *
 * So the note's own height is added back whenever it is showing, and the
 * test becomes one number against one threshold. It cannot oscillate: the
 * quantity being tested does not change when the answer changes.
 *
 * `note` is an upper bound on that height rather than a measurement of it.
 * Over-estimating leaves a card in the last few pixels of the band wearing
 * a note it only just fits; under-estimating would flicker, which is the
 * one outcome worth engineering against.
 */
export const useRoomFor = (spare: number, show = 72, note = 48) => {
  const [roomy, setRoomy] = React.useState(false);

  React.useEffect(() => {
    setRoomy((was) => (was ? spare + note : spare) >= show);
  }, [spare, show, note]);

  return roomy;
};
